import { FC, FormEvent, useState } from "react";
import { useNavigate } from "react-router";

import { Restricted } from "../../../../components/Restricted";
import { DETAILS_GRID_STYLES } from "../../detailedPages/styles";
import { Button } from "../../molecules/Button";
import { DetailedPages, DetailPagesTypes } from "../types";
import {
  actionDataFromEntity,
  generalPageWrapperData,
  pageWrapperDataByPageType,
} from "./constants";
import { VviinnWidgetProps } from "../../../vviinnWidget/types";
import { VviinnWidget } from "../../../vviinnWidget/VviinnWidget";
import { HeaderWithChildren } from "../../detailedPages";
import { BackLink } from "./BackLink";
import { unblockScroll } from "../../../../app/hooks/useBlockScroll";
import { SlideOverWrapper } from "../../organisms/SlideOver/SlideOverWrapper/SlideOverWrapper";
import { useNotifications } from "../../../../app/useNotifications";
import { OVERLAY_TEXT_BLOCK_STYLES } from "../../../styles";
import { VoidBooleanPromise } from "../../../../helpers/functions/types";

const FOOTER_BUTTON_STYLES = "grow sm:min-w-[150px] sm:grow-0 ";

const pageActionMessages = (
  detailedPageType: DetailPagesTypes,
  detailedPage: DetailedPages,
) => {
  const isNew = detailedPageType === "new";
  const afixes = isNew
    ? { info: "Creating", success: "created", error: "Could not create" }
    : { info: "Deleting", success: "deleted", error: "Could not delete" };
  const messages = {
    info: `${afixes.info}${isNew ? " new" : ""} ${detailedPage}`,
    success: `${isNew ? "New " : ""}${detailedPage} ${afixes.success}`,
    error: `${afixes.error} ${detailedPage}`,
  };
  return messages;
};

export const DetailedWrapper: FC<
  React.PropsWithChildren<{
    detailedPageType: DetailPagesTypes;
    detailedPage: DetailedPages;
    backLink?: string;
    deleteAction?: VoidBooleanPromise;
    createItemAction?: (event: FormEvent) => Promise<string | undefined>;
    widgetProps?: Omit<VviinnWidgetProps, "type">;
    customActionText?: string;
  }>
> = ({
  children,
  detailedPageType,
  detailedPage,
  backLink,
  deleteAction,
  createItemAction,
  widgetProps,
  customActionText,
}) => {
  const [actionSlideOver, setActionSlideOver] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { info, success, error } = pageActionMessages(
    detailedPageType,
    detailedPage,
  );

  const navigate = useNavigate();
  const { notify } = useNotifications();

  const handleCreateItem = async (event: FormEvent) => {
    if (!createItemAction) return;
    event.preventDefault();
    notify("Info", info);
    setIsSubmitted(true);
    try {
      const redirectLink = await createItemAction(event);
      if (redirectLink) {
        notify("Success", success);
        navigate(redirectLink, { replace: true });
      } else notify("Error", error);
    } catch (e) {
      notify("Error", error);
    } finally {
      setIsSubmitted(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteAction) return false;
    notify("Info", info);
    const isSuccess = await deleteAction();
    if (isSuccess) {
      notify("Success", success);
      if (backLink) {
        unblockScroll();
        navigate(backLink);
      }
    } else notify("Error", error);
    return isSuccess;
  };

  const { title, slideOverDeleteMessage } =
    pageWrapperDataByPageType[detailedPageType][detailedPage];
  const { buttonType, linkStyle, actionButtonTitle } =
    generalPageWrapperData[detailedPageType];

  const { actionPermissions, actionText, actionModalMessage } =
    actionDataFromEntity(detailedPage, detailedPageType === "new");

  const isCompanyDetails =
    detailedPage === "company" && detailedPageType === "detail";

  const ContentAndFooter = (
    <>
      {children}

      <div className="flex justify-between gap-6 border-t empty:hidden border-stone-200 pt-6 dark:border-stone-800 sm:justify-end sm:gap-4">
        {widgetProps && (
          <VviinnWidget
            type="vviinn-vps-button"
            classNames=""
            customId="vviinn-vps-button-container"
            {...widgetProps}
          >
            <Button
              id="testSettingsButton"
              title="Test Settings"
              buttonType="tetriary"
              className={`-mr-1.5 h-full ${FOOTER_BUTTON_STYLES}`}
            />
          </VviinnWidget>
        )}
        {backLink && !isCompanyDetails && (
          <BackLink backLink={backLink} style={linkStyle} />
        )}

        <Restricted permissions={actionPermissions}>
          <Button
            title={actionText}
            buttonType={buttonType}
            type="submit"
            onClick={deleteAction ? () => setActionSlideOver(true) : undefined}
            disabled={isSubmitted}
            className={FOOTER_BUTTON_STYLES}
          />
          {deleteAction && slideOverDeleteMessage && (
            <SlideOverWrapper
              title={customActionText ?? actionModalMessage}
              open={actionSlideOver}
              onClose={() => setActionSlideOver(false)}
              buttonProps={{
                title: actionButtonTitle,
                onClick: handleDelete,
                buttonType,
              }}
            >
              <div className={OVERLAY_TEXT_BLOCK_STYLES}>
                {slideOverDeleteMessage}
              </div>
            </SlideOverWrapper>
          )}
        </Restricted>
      </div>
    </>
  );

  return (
    <>
      {backLink && !isCompanyDetails && (
        <BackLink backLink={backLink} style="link" />
      )}

      <HeaderWithChildren title={title}>
        {widgetProps && (
          <VviinnWidget type="vviinn-vps-button" {...widgetProps} />
        )}
      </HeaderWithChildren>

      {createItemAction ? (
        <form className={DETAILS_GRID_STYLES} onSubmit={handleCreateItem}>
          {ContentAndFooter}
        </form>
      ) : (
        <div className={DETAILS_GRID_STYLES}>{ContentAndFooter}</div>
      )}
    </>
  );
};
